import type { AffinityDb } from "../database.ts";
import { addDateAnchor } from "../dates/add_date_anchor.ts";
import { removeDateAnchor } from "../dates/remove_date_anchor.ts";
import { reviseDateAnchor } from "../dates/revise_date_anchor.ts";
import type {
  AddDateAnchorInput,
  DateAnchorTarget,
} from "../lib/types/add_date_anchor_input.ts";
import type { EventRecord } from "../lib/types/event_record.ts";
import type { ReviseDateAnchorOptions } from "../lib/types/revise_date_anchor_options.ts";
import type { ReviseDateAnchorPatch } from "../lib/types/revise_date_anchor_patch.ts";
import type { TimeInputs } from "./parse_time_input.ts";
import {
  booleanSchema,
  defineAffinityTool,
  enumSchema,
  integerSchema,
  objectSchema,
  stringSchema,
} from "./tool_metadata.ts";
import { type MutationToolData, mutationToolResult } from "./tool_mutation.ts";
import { manageDateAnchorToolName } from "./tool_names.ts";
import {
  type ContactLocator,
  type LinkLocator,
  resolveContactLocator,
  resolveLinkLocator,
  withToolHandling,
} from "./tool_resolvers.ts";
import { toolFailure as invalidToolInput } from "./tool_types.ts";
import type {
  ToolFailure,
  ToolNeedsClarification,
  ToolResult,
} from "./tool_types.ts";
import { validateToolInput } from "./validate_tool_input.ts";

type DateAnchorTargetInput =
  | { kind: "contact"; contact: ContactLocator }
  | { kind: "link"; link: LinkLocator };

export type ManageDateAnchorToolInput =
  | {
      action: "add";
      input: Omit<AddDateAnchorInput, "target"> & {
        target: DateAnchorTargetInput;
      };
    }
  | {
      action: "revise";
      anchorEventId: number;
      patch: ReviseDateAnchorPatch;
      options?: ReviseDateAnchorOptions;
    }
  | { action: "remove"; anchorEventId: number; removedAt?: number };

export type ManageDateAnchorToolResult = ToolResult<
  MutationToolData<EventRecord>
>;

function contactLocatorSchema(description: string) {
  return {
    type: "object" as const,
    properties: {
      contactId: integerSchema("Exact contact id."),
      identity: objectSchema(
        {
          type: stringSchema("Identity type."),
          value: stringSchema("Identity value."),
        },
        ["type", "value"],
        "Contact identity locator.",
      ),
    },
    description,
  };
}

function linkLocatorSchema(description: string) {
  return {
    type: "object" as const,
    properties: {
      linkId: integerSchema("Exact link id."),
      endpoints: objectSchema(
        {
          fromContactId: integerSchema("From contact id."),
          toContactId: integerSchema("To contact id."),
          kind: stringSchema("Optional link kind."),
          role: stringSchema("Optional link role."),
          isStructural: booleanSchema("Optional structural discriminator."),
        },
        ["fromContactId", "toContactId"],
        "Endpoint-based link locator.",
      ),
    },
    description,
  };
}

function resolveDateAnchorTarget(
  db: AffinityDb,
  target: DateAnchorTargetInput,
):
  | { kind: "ok"; target: DateAnchorTarget }
  | {
      kind: "error";
      result: ToolFailure | ToolNeedsClarification;
    } {
  if (target.kind === "contact") {
    const contact = resolveContactLocator(db, target.contact, "target.contact");
    if (!contact.ok) {
      return { kind: "error", result: contact.result };
    }
    return {
      kind: "ok",
      target: { kind: "contact", contactId: contact.value.id },
    };
  }
  const link = resolveLinkLocator(db, target.link, "target.link");
  if (!link.ok) {
    return { kind: "error", result: link.result };
  }
  return { kind: "ok", target: { kind: "link", linkId: link.value.id } };
}

export function manageDateAnchorToolHandler(
  db: AffinityDb,
  rawInput: TimeInputs<ManageDateAnchorToolInput>,
): ManageDateAnchorToolResult {
  const input = structuredClone(rawInput) as ManageDateAnchorToolInput;
  const inputError = validateToolInput(manageDateAnchorTool.inputSchema, input);
  if (inputError)
    return invalidToolInput(
      "protocol",
      "invalid_input",
      "Invalid input.",
      inputError,
    );
  if (input.action !== "add" && input.anchorEventId === undefined)
    return invalidToolInput(
      "protocol",
      "invalid_input",
      "Missing anchorEventId.",
      "anchorEventId is required.",
    );
  return withToolHandling<MutationToolData<EventRecord>>(() => {
    if (input.action === "add") {
      if (!input.input)
        return invalidToolInput(
          "protocol",
          "invalid_input",
          "Missing input.",
          "input is required for add.",
        );
      const resolved = resolveDateAnchorTarget(db, input.input.target);
      if (resolved.kind === "error") {
        return resolved.result;
      }
      return mutationToolResult(
        "add",
        addDateAnchor(db, { ...input.input, target: resolved.target }),
        "date anchor",
      );
    }
    if (input.action === "revise") {
      return mutationToolResult(
        "revise",
        reviseDateAnchor(db, input.anchorEventId, input.patch, input.options),
        "date anchor",
        Object.keys(input.patch),
      );
    }
    return mutationToolResult(
      "remove",
      removeDateAnchor(db, input.anchorEventId, input.removedAt),
      "date anchor",
    );
  }, "Date-anchor tool failed.");
}

export const manageDateAnchorTool = defineAffinityTool<
  TimeInputs<ManageDateAnchorToolInput>,
  ManageDateAnchorToolResult
>({
  name: manageDateAnchorToolName,
  description:
    "Add, revise, or remove recurring date anchors on contacts or links. Use this for birthdays, anniversaries, and yearly events — not manage_attribute.",
  whenToUse:
    "Use this for birthdays, anniversaries, renewals, memorials, and other recurring anchors.",
  whenNotToUse:
    "Do not use this for one-off events or relationship state changes.",
  sideEffects: "writes_state",
  readOnly: false,
  supportsClarification: true,
  targetKinds: ["contact", "link", "event"],
  inputDescriptions: {
    action: "Which date-anchor action to perform.",
    input: "Creation payload for a new date anchor.",
    anchorEventId: "Exact anchor event id for revise or remove actions.",
    patch: "Allowed date-anchor patch fields.",
    options: "Optional revise metadata such as now and force.",
    removedAt: "Optional removal timestamp.",
  },
  outputDescription:
    "Returns the date-anchor mutation receipt, including any recomputed upcoming occurrences.",
  inputSchema: objectSchema(
    {
      action: enumSchema("Operation to perform.", ["add", "revise", "remove"]),
      input: objectSchema(
        {
          target: objectSchema(
            {
              kind: enumSchema("Target kind.", ["contact", "link"]),
              contact: contactLocatorSchema(
                "Contact locator. Required when kind=contact. Provide contactId or identity.",
              ),
              link: linkLocatorSchema(
                "Link locator. Required when kind=link. Provide linkId or endpoints.",
              ),
            },
            ["kind"],
            "Date-anchor target.",
          ),
          recurrenceKind: enumSchema("Recurrence kind.", [
            "birthday",
            "anniversary",
            "renewal",
            "memorial",
            "custom_yearly",
          ]),
          anchorMonth: integerSchema("Anchor month, 1–12."),
          anchorDay: integerSchema("Anchor day, 1–31 (validated per month)."),
          summary: stringSchema("Summary. Must be non-empty."),
          significance: integerSchema("Significance from 1 to 10."),
          now: integerSchema("Optional timestamp override."),
          force: booleanSchema("Whether to bypass duplicate checks."),
        },
        [
          "target",
          "recurrenceKind",
          "anchorMonth",
          "anchorDay",
          "summary",
          "significance",
        ],
        "Date anchor payload. Required when action=add.",
      ),
      anchorEventId: integerSchema(
        "Anchor event id. Required when action=revise or remove.",
      ),
      patch: objectSchema(
        {
          recurrenceKind: enumSchema("Optional recurrence kind.", [
            "birthday",
            "anniversary",
            "renewal",
            "memorial",
            "custom_yearly",
          ]),
          anchorMonth: integerSchema("Optional month, 1–12."),
          anchorDay: integerSchema("Optional day, 1–31."),
          summary: stringSchema("Optional summary."),
          significance: integerSchema("Optional significance, 1–10."),
        },
        [],
        "Anchor patch. Required when action=revise.",
      ),
      options: objectSchema(
        {
          now: integerSchema("Optional timestamp."),
          force: booleanSchema("Whether duplicate checks should be bypassed."),
        },
        [],
        "Optional metadata.",
      ),
      removedAt: integerSchema(
        "Removal timestamp. Optional for action=remove.",
      ),
    },
    ["action"],
    "Add, revise, or remove recurring date anchors.",
  ),
  handler: manageDateAnchorToolHandler,
});
